import React, { useState, useEffect, useRef } from "react";
import { Form, Input, Button, Card, Typography, message } from "antd";
import axios from "axios";
import logo from "../assets/images/logo.png";
import { Link } from "react-router-dom";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const { Title, Text } = Typography;

const Login = () => {
  const [loading, setLoading] = useState(false);
  const isMounted = useRef(true);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      isMounted.current = false;
    };
  }, []);

  const onFinish = async (values) => {
    setLoading(true);
    const { username, password } = values;

    try {
      const response = await axios.post(`${API_BASE_URL}/admin/login`, {
        admin_id: username,
        password,
      });

      if (response.status === 200) {
        message.success("Admin login successful!");
        localStorage.setItem("isLoggedIn", "true");
        localStorage.setItem("adminId", username);
        localStorage.setItem("essar_iam_email", username);
        window.location.replace("/dashboard");
      } else {
        message.error("Invalid username or password!");
      }
    } catch (error) {
      if (error.response) {
        message.error(error.response.data.detail || "Login failed!");
      } else {
        message.error("Unable to connect to the server. Please try again later.");
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  const isMobile = windowWidth < 576;

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        background: "linear-gradient(135deg, #007cf0 0%, #7928ca 100%)",
        position: "relative",
        padding: "16px",
      }}
    >
      {/* Subtle dot pattern overlay */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.05,
          backgroundImage: "radial-gradient(circle, #ffffff 1px, transparent 1px)",
          backgroundSize: "20px 20px",
          pointerEvents: "none",
        }}
      />

      <Card
        style={{
          width: isMobile ? "100%" : 420,
          maxWidth: 440,
          borderRadius: 24,
          background: "rgba(255,255,255,0.9)",
          backdropFilter: "blur(20px)",
          boxShadow: "0 20px 60px rgba(0,0,0,0.15), 0 8px 20px rgba(0,0,0,0.1)",
          border: "1px solid rgba(255,255,255,0.5)",
          textAlign: "center",
          padding: isMobile ? "24px 20px" : "32px 28px",
          animation: "fadeInUp 0.6s ease",
        }}
        bodyStyle={{ padding: 0 }}
      >
        <img
          src={logo}
          alt="Logo"
          style={{ width: isMobile ? 80 : 100, marginBottom: 20 }}
        />

        <Title
          level={isMobile ? 3 : 2}
          style={{
            color: "#1f1f1f",
            fontWeight: 700,
            marginBottom: 8,
            letterSpacing: -0.5,
          }}
        >
          Welcome to IAM
        </Title>

        <Text
          type="secondary"
          style={{
            display: "block",
            marginBottom: 24,
            fontSize: isMobile ? 14 : 16,
            color: "#555",
          }}
        >
          Please log in to access your dashboard
        </Text>

        <Form
          name="login"
          layout="vertical"
          onFinish={onFinish}
          size="large"
          style={{ textAlign: "left" }}
        >
          <Form.Item
            label={
              <span style={{ fontWeight: 600, color: "#1f1f1f", fontSize: 13 }}>
                Admin ID
              </span>
            }
            name="username"
            rules={[{ required: true, message: "Please input your Admin ID!" }]}
          >
            <Input
              placeholder="Enter your Admin ID"
              style={{
                borderRadius: 12,
                padding: "10px 14px",
                border: "1px solid #e0e0e0",
                transition: "all 0.2s",
              }}
            />
          </Form.Item>

          <Form.Item
            label={
              <span style={{ fontWeight: 600, color: "#1f1f1f", fontSize: 13 }}>
                Password
              </span>
            }
            name="password"
            rules={[{ required: true, message: "Please input your password!" }]}
          >
            <Input.Password
              placeholder="Enter your password"
              style={{
                borderRadius: 12,
                padding: "10px 14px",
                border: "1px solid #e0e0e0",
              }}
            />
          </Form.Item>

          <Text
            style={{
              display: "block",
              textAlign: "right",
              marginBottom: 16,
              fontSize: 13,
            }}
          >
            <Link
              to="/forgot-password"
              style={{
                color: "#007cf0",
                fontWeight: 500,
                textDecoration: "underline",
              }}
            >
              Forgot Password?
            </Link>
          </Text>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              block
              size="large"
              style={{
                borderRadius: 12,
                height: 48,
                fontWeight: 600,
                fontSize: 16,
                background: "linear-gradient(135deg, #007cf0 0%, #7928ca 100%)",
                border: "none",
                boxShadow: "0 4px 14px rgba(0,124,240,0.3)",
                transition: "all 0.3s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = "0 6px 20px rgba(0,124,240,0.4)";
                e.currentTarget.style.transform = "translateY(-1px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = "0 4px 14px rgba(0,124,240,0.3)";
                e.currentTarget.style.transform = "none";
              }}
            >
              Login
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {/* simple fade-in animation */}
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default Login;